import { getDb } from "./firebase-lazy";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  doc,
  setDoc,
  type Timestamp,
} from "firebase/firestore";
import type { VocalAnalysisResult } from "@/components/ai/VocalAnalysisReport";
import type {
  MusicDNAResult,
  MusicDNAMetrics,
} from "@/lib/music-dna/analyzeMusicDNA";

export interface SavedAnalysisResult {
  id: string;
  userId: string;
  fileName: string;
  analysisType: "vocal" | "instrument" | "composition";
  tier: "free" | "advanced";
  result: VocalAnalysisResult;
  audioUrl?: string;
  trackName?: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  musicDNA?: MusicDNAFirestore;
}

export interface MusicDNAFirestore {
  key: string;
  label: string;
  emoji: string;
  title: string;
  description: string;
  tagline: string;
  metrics?: MusicDNAMetrics;
}

/**
 * 분석 결과를 Firestore에 저장
 */
function removeUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(removeUndefined) as unknown as T;
  }

  if (value && typeof value === "object") {
    const cleaned: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, val]) => {
      if (val !== undefined) {
        cleaned[key] = removeUndefined(val);
      }
    });
    return cleaned as unknown as T;
  }

  return value;
}

export async function saveAnalysisResult(
  analysisId: string | null,
  userId: string,
  fileName: string,
  analysisType: "vocal" | "instrument" | "composition",
  tier: "free" | "advanced",
  result: VocalAnalysisResult,
  audioUrl?: string
): Promise<{ success: boolean; analysisId?: string; error?: string }> {
  try {
    const db = getDb();
    const sanitizedResult = removeUndefined(result);
    const musicDNAData = result.musicDNA
      ? normalizeMusicDNA(result.musicDNA)
      : undefined;

    const analysisData = {
      userId,
      fileName,
      analysisType,
      tier,
      result: sanitizedResult,
      audioUrl: audioUrl || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...(musicDNAData ? { musicDNA: musicDNAData } : {}),
    };

    const finalAnalysisId =
      typeof analysisId === "string" && analysisId.trim()
        ? analysisId.trim()
        : null;

    if (finalAnalysisId) {
      await setDoc(doc(db, "analysisResults", finalAnalysisId), analysisData, {
        merge: true,
      });
      console.log("✅ 분석 결과 저장 완료:", finalAnalysisId);
    } else {
      const docRef = await addDoc(
        collection(db, "analysisResults"),
        analysisData
      );
      console.log("✅ 분석 결과 저장 완료:", docRef.id);
      analysisId = docRef.id;
    }

    const legacyId =
      finalAnalysisId || (typeof analysisId === "string" ? analysisId : "");
    if (musicDNAData && legacyId) {
      try {
        await setDoc(
          doc(db, "analysis_results", legacyId),
          {
            userId,
            analysisType,
            tier,
            tempo: musicDNAData.metrics?.tempo ?? null,
            rms: musicDNAData.metrics?.dynamicRange ?? null,
            centroid: musicDNAData.metrics?.spectralCentroid ?? null,
            emotion: musicDNAData.metrics?.emotion ?? null,
            genre: musicDNAData.metrics?.genre ?? null,
            musicDNA: {
              type: musicDNAData.title,
              emoji: musicDNAData.emoji,
              description: musicDNAData.description,
              tagline: musicDNAData.tagline,
            },
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (legacyError) {
        console.warn("⚠️ analysis_results 컬렉션 업데이트 실패:", legacyError);
      }
    }

    return {
      success: true,
      analysisId: finalAnalysisId || (analysisId as string),
    };
  } catch (error) {
    console.error("❌ 분석 결과 저장 실패:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "알 수 없는 오류",
    };
  }
}

/**
 * 사용자의 저장된 분석 결과 목록 조회
 */
export async function getUserAnalysisResults(
  userId: string,
  limitCount: number = 20
): Promise<{
  success: boolean;
  results?: SavedAnalysisResult[];
  error?: string;
}> {
  try {
    const db = getDb();
    const q = query(
      collection(db, "analysisResults"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const results: SavedAnalysisResult[] = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      results.push({
        id: doc.id,
        userId: data.userId as string,
        fileName: data.fileName as string,
        analysisType: data.analysisType as SavedAnalysisResult["analysisType"],
        tier: data.tier as SavedAnalysisResult["tier"],
        result: data.result as VocalAnalysisResult,
        audioUrl: data.audioUrl ?? undefined,
        trackName: data.trackName ?? undefined,
        createdAt: (data.createdAt ?? null) as Timestamp | null,
        updatedAt: (data.updatedAt ?? null) as Timestamp | null,
        musicDNA: data.musicDNA as MusicDNAFirestore | undefined,
      });
    });

    return { success: true, results };
  } catch (error) {
    console.error("❌ 분석 결과 조회 실패:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "알 수 없는 오류",
    };
  }
}

function normalizeMusicDNA(dna: MusicDNAResult): MusicDNAFirestore {
  // metrics에서 undefined 값 제거
  const cleanedMetrics = dna.metrics
    ? Object.entries(dna.metrics).reduce(
        (acc, [key, value]) => {
          if (value !== undefined) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, unknown>
      )
    : undefined;

  const normalized: MusicDNAFirestore = {
    key: dna.key,
    label: dna.label,
    emoji: dna.emoji,
    title: dna.title,
    description: dna.description,
    tagline: dna.tagline,
    ...(cleanedMetrics && Object.keys(cleanedMetrics).length > 0
      ? { metrics: cleanedMetrics as MusicDNAMetrics }
      : {}),
  };

  // 최종적으로 undefined 값 제거
  return removeUndefined(normalized) as MusicDNAFirestore;
}

/**
 * 분석 결과의 곡 제목 업데이트
 */
export async function updateAnalysisTrackName(
  analysisId: string,
  trackName: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const db = getDb();
    await setDoc(
      doc(db, "analysisResults", analysisId),
      {
        trackName: trackName || null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    return { success: true };
  } catch (error) {
    console.error("❌ 곡 제목 업데이트 실패:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "알 수 없는 오류",
    };
  }
}
